<template>
    <div class="member-review-entry">
        <div class="entry-card" v-if="isLoading">
            <b-spinner small></b-spinner> Loading...
        </div>
        <div class="entry-card" v-else-if="fatalError">
            <h2>Verify your identity</h2>
            <div class="entry-error">{{ fatalError }}</div>
        </div>
        <div class="entry-card" v-else>
            <h2>Verify your identity</h2>
            <p v-if="sentTo.length">
                Enter the 6-digit code we sent to <strong>{{ sentTo.join(', ') }}</strong> to continue to the enrollment review.
            </p>
            <p v-else>Enter the 6-digit code we sent you to continue to your enrollment review.</p>
            <label class="otp-label">Enter Code<span class="required-label">*</span></label>
            <v-otp-input
                ref="otpInput"
                input-classes="otp-input"
                separator="-"
                :num-inputs="6"
                :should-auto-focus="true"
                :is-input-num="true"
                @on-change="onChange"
                @on-complete="onChange"
            />
            <div class="entry-error" v-if="error">{{ error }}</div>
            <button type="button" class="entry-submit" :disabled="!isComplete || isVerifying" @click="verify">
                <b-spinner v-if="isVerifying" small></b-spinner>
                Verify
            </button>
            <login-otp :expirySecond="resendCooldownSeconds" :setSpinnerLoader="isResending" @sendOTP="resend" />
        </div>
    </div>
</template>

<script>
import NewLoginOtp from '@/views/NewLogin/NewLoginOtp.vue';
import reviewApi from '@/resource/memberReviewApi';
import session from '@/resource/memberReviewSession';
import { notify, errorMessage } from '@/resource/memberReviewNotify';

// Must match MemberReviewOtpService::RESEND_COOLDOWN_SECONDS in corenroll-api.
const RESEND_COOLDOWN_SECONDS = 150;
const STORAGE_BLOCKED = 'Your browser is blocking site storage. Please allow it for this site, or try a different browser.';

export default {
    name: 'MemberReviewEntry',
    components: {
        'login-otp': NewLoginOtp,
    },
    data: () => ({
        code: '',
        error: '',
        fatalError: '',
        sentTo: [],
        isLoading: true,
        isVerifying: false,
        isResending: false,
        resendCooldownSeconds: RESEND_COOLDOWN_SECONDS,
    }),
    computed: {
        isComplete() {
            return /^\d{6}$/.test(this.code);
        },
    },
    mounted() {
        this.start();
    },
    methods: {
        async start() {
            const token = this.$route.params.token;
            // A different link than the stored one (another policy): drop the old tokens.
            if (session.get() !== token) {
                session.clear();
            }
            if (!session.set(token)) {
                this.fatalError = STORAGE_BLOCKED;
                this.isLoading = false;
                return;
            }

            try {
                const res = await reviewApi.getOtpStatus();
                const data = (res.data && res.data.data) || {};
                this.sentTo = data.sent_to || [];
                if (data.verified && session.getAccess()) {
                    this.$router.replace({ name: 'MemberReview' });
                    return;
                }
                this.isLoading = false;
                await this.send(true);
            } catch (err) {
                // 401 (bad link) is redirected by memberReviewApi. 422 = no email on file.
                this.fatalError = errorMessage(err);
                this.isLoading = false;
            }
        },
        onChange(value) {
            this.code = value || '';
            this.error = '';
        },
        async verify() {
            if (!this.isComplete || this.isVerifying) return;
            this.isVerifying = true;
            this.error = '';
            try {
                const { accessToken } = await reviewApi.verifyOtp(this.code);
                if (!session.setAccess(accessToken)) {
                    this.error = STORAGE_BLOCKED;
                    return;
                }
                this.$router.replace({ name: 'MemberReview' });
            } catch (err) {
                // Clear the boxes first: clearInput() emits on-change(''), and onChange
                // resets the error, so setting the message before it would wipe it.
                if (this.$refs.otpInput && this.$refs.otpInput.clearInput) {
                    this.$refs.otpInput.clearInput();
                }
                this.code = '';
                this.error = errorMessage(err);
            } finally {
                this.isVerifying = false;
            }
        },
        async send(isFirst = false) {
            this.isResending = true;
            try {
                const res = await reviewApi.sendOtp();
                const data = (res.data && res.data.data) || {};
                if (data.sent_to) this.sentTo = data.sent_to;
                if (!isFirst) notify(this, 'success', (res.data && res.data.message) || 'A new code has been sent.');
            } catch (err) {
                const status = err && err.response && err.response.status;
                // On page load a 429 usually means a code was just sent (page refreshed): not an error.
                notify(this, isFirst && status === 429 ? 'info' : 'error', errorMessage(err));
            } finally {
                this.isResending = false;
            }
        },
        resend() {
            return this.send(false);
        },
    },
};
</script>

<style scoped>
.member-review-entry {
    background: #f3f6f8;
    padding: 5rem 1rem;
    min-height: 60vh;
}
.entry-card {
    max-width: 480px;
    margin: 0 auto;
    background: #fff;
    border-radius: 15px;
    box-shadow: 0 2px 15px rgb(216 227 235 / 33%);
    padding: 2rem;
}
.entry-card h2 {
    font-size: 22px;
    color: #2c3f58;
    font-weight: 700;
}
.entry-card p {
    color: #778699;
    font-size: 14px;
}
.otp-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
}
.required-label {
    color: #ff0000;
}
.entry-card >>> .otp-input {
    width: 44px;
    height: 44px;
    margin: 0 4px;
    font-size: 18px;
    text-align: center;
    border: 1px solid #dce1e7;
    border-radius: 6px;
}
.entry-error {
    color: #dc3545;
    font-size: 13px;
    margin-top: 10px;
}
.entry-submit {
    width: 100%;
    margin: 20px 0 15px;
    padding: 10px 0;
    border: 0;
    border-radius: 6px;
    color: #fff;
    background: linear-gradient(#0cf, #1597b8 49%, #0567bc);
}
.entry-submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>
